import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import GameModal from '../components/GameModal';
import TranslateConfirmModal from '../components/TranslateConfirmModal';
import { getLocaleStrings, t as localeT } from '../common/localization';
import TranslateErrorModal from '../components/TranslateErrorModal';
import TranslationProgressModal from '../components/TranslationProgressModal';
import ProperGameInfo from '../common/typedef/ProperGameInfo';
import GameMgr from '../common/GameMgr';
import { useLanguage } from './LanguageContext';
import { useGameTranslation } from '../hooks/useGameTranslation';
import Network from '../common/networkHandler/Network';
import { hasNewStoryFormat } from '../common/translation/story-format-check';
import { hasLanguagePack } from '../common/translation/language-pack-check';
import { AgeConfirm } from '../common/util/AgeConfirm';
import Auth from '../common/auth/auth';

interface GameModalContextType {
  openGameModal: (gameInfo: ProperGameInfo) => void;
  closeGameModal: () => void;
  handlePlayGame: (gameInfo: ProperGameInfo) => Promise<void>;
}

const GameModalContext = createContext<GameModalContextType | undefined>(undefined);

interface GameModalProviderProps {
  children: ReactNode;
}

export const GameModalProvider: React.FC<GameModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [gameId, setGameId] = useState<number | null>(null);
  const [gameName, setGameName] = useState<string>('');
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);

  // When set, the iframe player loads this language instead of the user's UI
  // language. Used to play a story in its origin language when no localization
  // pack is available, or when the user opts out of an available translation.
  const [gameLanguageOverride, setGameLanguageOverride] = useState<string | null>(null);

  // Confirm modal state. The popup serves two purposes depending on the user
  // and pack state:
  //   - Pack already on platform → confirm just loads the pack (no AI).
  //   - Pack missing + authed user → confirm triggers AI translation.
  //   - Pack missing + guest user → no popup at all (guest can't pay AI).
  // `pendingPackExists` tells the confirm handler which path to take so we
  // don't have to re-fetch the pack file.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingGameInfo, setPendingGameInfo] = useState<ProperGameInfo | null>(null);
  const [pendingPackExists, setPendingPackExists] = useState(false);

  // Rating warning state
  const [showRatingWarn, setShowRatingWarn] = useState(false);
  const [ratingWarnGame, setRatingWarnGame] = useState<ProperGameInfo | null>(null);
  
  const { language, getLanguageName } = useLanguage();

  console.log('GameModalProvider - Current language:', language);

  // Translation hook drives the in-flight progress / error modals when an
  // authenticated user opts into AI translation from the confirm popup.
  const { translationState, translateGame, clearTranslationState } = useGameTranslation();

  // Effect to handle language changes
  useEffect(() => {
    console.log('GameModalProvider - Language changed to:', language);
    // Clear any pending translation state when language changes
    if (translationState.isTranslating) {
      clearTranslationState();
    }
  }, [language, clearTranslationState]);

  // Discover ai-service URL early so it's cached when user clicks Play
  useEffect(() => {
    Network.withServerAssistant().resolveAiServiceUrl().catch(() => {
      // Non-fatal — buildPlayerUrl falls back to env var
    });
  }, []);

  const needsRatingWarn = (gameInfo: ProperGameInfo) => {
    const r = gameInfo.originalGameInfo?.rating?.toUpperCase();
    if (r !== 'T' && r !== 'M') return false;
    return !AgeConfirm.isConfirmed(r);
  };

  const proceedPlay = async (gameInfo: ProperGameInfo) => {
    const orig = gameInfo.originalGameInfo;
    const originLanguage = orig?.originLanguage;
    const willShowPopup = !!orig && !!originLanguage && originLanguage !== language;

    let skipReason: string | null = null;
    if (!orig) skipReason = 'no originalGameInfo on ProperGameInfo';
    else if (!originLanguage) skipReason = 'originLanguage missing in API response (backend did not populate it for this game)';
    else if (originLanguage === language) skipReason = `originLanguage (${originLanguage}) matches userLanguage (${language})`;

    console.log('[Translation] handlePlayGame check:', {
      gameId: gameInfo.id,
      hasOriginalGameInfo: !!orig,
      originLanguage,
      userLanguage: language,
      localized: orig?.localized,
      willShowPopup,
      skipReason,
      rawGameInfoKeys: orig ? Object.keys(orig) : null,
    });

    if (!willShowPopup && orig) {
      console.warn('[Translation] Translation popup NOT shown. Reason:', skipReason);
    }

    // Same language as user (or no origin info) → open directly.
    if (!willShowPopup) {
      openGameModalWithLanguage(gameInfo, language);
      return;
    }

    const [isNewFormat, packExists] = await Promise.all([
      hasNewStoryFormat(gameInfo.id),
      hasLanguagePack(gameInfo.id, language),
    ]);

    // Legacy stories cannot be translated and never have packs.
    if (!isNewFormat) {
      console.log('[Translation] Legacy story — opening in origin language');
      openGameModalWithLanguage(gameInfo, originLanguage as string);
      return;
    }

    // Guests can't trigger AI translation. Show the popup only when the pack
    // already exists (so they can pick translated vs. origin); otherwise
    // silently open the source story.
    const isGuest = Auth.isLoginGuest();
    if (!packExists && isGuest) {
      console.log('[Translation] Guest + no pack — opening in origin language');
      openGameModalWithLanguage(gameInfo, originLanguage as string);
      return;
    }

    // Show the popup in two cases:
    //   - Pack exists (any user)  → confirm reuses it without AI.
    //   - Pack missing + authed   → confirm triggers translateStory().
    setPendingGameInfo(gameInfo);
    setPendingPackExists(packExists);
    setShowConfirmModal(true);
  };

  const openGameModalWithLanguage = (gameInfo: ProperGameInfo, lang: string) => {
    setGameLanguageOverride(lang === language ? null : lang);
    openGameModal(gameInfo);
  };

  const handlePlayGame = async (gameInfo: ProperGameInfo) => {
    if (needsRatingWarn(gameInfo)) {
      setRatingWarnGame(gameInfo);
      setShowRatingWarn(true);
      return;
    }
    await proceedPlay(gameInfo);
  };

  // Confirm flow:
  //   - Pack exists  → no AI, just open with the user's language (iframe
  //                    loads {lang}.json from the platform).
  //   - Pack missing → trigger AI translation via translateStory(), then open
  //                    once the platform has the new pack. Reachable only for
  //                    authenticated users (proceedPlay gates guests out).
  const handleConfirmTranslate = async () => {
    if (!pendingGameInfo) return;
    setShowConfirmModal(false);

    if (pendingPackExists) {
      openGameModalWithLanguage(pendingGameInfo, language);
      setPendingGameInfo(null);
      return;
    }

    const result = await translateGame(pendingGameInfo.originalGameInfo);
    if (result.success) {
      openGameModalWithLanguage(pendingGameInfo, language);
    } else {
      const fallback =
        pendingGameInfo.originalGameInfo?.originLanguage || language;
      setTimeout(() => {
        openGameModalWithLanguage(pendingGameInfo, fallback);
      }, 2000);
    }
    setPendingGameInfo(null);
  };

  // User prefers the origin language — open with the story's origin code so
  // the iframe loads the source text without applying the localization pack.
  const handleNoTranslate = () => {
    if (!pendingGameInfo) {
      setShowConfirmModal(false);
      return;
    }
    const originLanguage =
      pendingGameInfo.originalGameInfo?.originLanguage || language;
    setShowConfirmModal(false);
    openGameModalWithLanguage(pendingGameInfo, originLanguage);
    setPendingGameInfo(null);
  };

  const handleCancelTranslate = () => {
    setShowConfirmModal(false);
    setPendingGameInfo(null);
  };

  const openGameModal = (gameInfo: ProperGameInfo) => {
    setGameId(gameInfo.id);
    setGameName(gameInfo.displayName || '');
    setIsOpen(true);
    GameMgr.viewGame(gameInfo.id);
    
    // Start the timer
    setGameStartTime(Date.now());

    console.log(`Game ${gameInfo.id} - ${gameInfo.displayName} started at:`, new Date().toISOString());
  };

  const closeGameModal = () => {
    trackGamePlayTime();
    setIsOpen(false);
    setGameLanguageOverride(null);
  };

  const trackGamePlayTime = async () => {
    try {
      if (gameId !== null && gameStartTime !== null) {
        const endTime = Date.now();
        const playTimeInSeconds = Math.floor((endTime - gameStartTime) / 1000);
 
        console.log(`Game ${gameId} - ${gameName} play session ended. Play time: ${playTimeInSeconds} seconds`);
        
        if (playTimeInSeconds > 0) {
          await GameMgr.logGamePlayTime(gameId, playTimeInSeconds);
        }

        setGameStartTime(null);
      }
    } catch (error) {
      console.error('Failed to log play time:', error);
    }
  };

  // Handle window close/refresh events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isOpen) {
        trackGamePlayTime();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, gameId, gameStartTime, gameName]);

  return (
    <GameModalContext.Provider value={{ openGameModal, closeGameModal, handlePlayGame }}>
      {children}
      {gameId !== null && (
        <GameModal
          gameId={gameId}
          gameName={gameName}
          open={isOpen}
          onClose={closeGameModal}
          language={gameLanguageOverride ?? undefined}
        />
      )}
      
      {/* Translation Progress Modal */}
      <TranslationProgressModal
        open={translationState.isTranslating}
        progress={translationState.progress}
      />
      
      {/* Translation Error Modal */}
      <TranslateErrorModal
        open={!!translationState.error}
        errorMessage={translationState.error ?? ''}
        onClose={clearTranslationState}
      />

      {/* Rating Warning Dialog */}
      {showRatingWarn && ratingWarnGame && (
        <RatingWarningDialog
          gameInfo={ratingWarnGame}
          language={language}
          onContinue={() => {
            setShowRatingWarn(false);
            const g = ratingWarnGame;
            const r = g.originalGameInfo?.rating;
            if (r) AgeConfirm.confirm(r);
            setRatingWarnGame(null);
            proceedPlay(g);
          }}
          onCancel={() => {
            setShowRatingWarn(false);
            setRatingWarnGame(null);
          }}
        />
      )}

      {/* Language Confirmation Modal */}
      {showConfirmModal && pendingGameInfo && pendingGameInfo.originalGameInfo?.originLanguage && (
        <TranslateConfirmModal
          open={showConfirmModal}
          originCode={pendingGameInfo.originalGameInfo.originLanguage}
          originLanguage={getLanguageName(pendingGameInfo.originalGameInfo.originLanguage as any)}
          targetCode={language}
          targetLanguage={getLanguageName(language)}
          onConfirm={handleConfirmTranslate}
          onKeep={handleNoTranslate}
          onClose={handleCancelTranslate}
        />
      )}
    </GameModalContext.Provider>
  );
};

const RATING_COLORS: Record<string, { bg: string; border: string }> = {
  T: { bg: 'rgba(217,119,6,0.12)', border: '#D97706' },
  M: { bg: 'rgba(220,38,38,0.12)', border: '#DC2626' },
};

function RatingWarningDialog({
  gameInfo, language, onContinue, onCancel,
}: {
  gameInfo: ProperGameInfo;
  language: string;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const rating = (gameInfo.originalGameInfo?.rating ?? '').toUpperCase();
  const s = getLocaleStrings(language);
  const title = localeT(s, 'rating_warn_title', { rating });
  const body = rating === 'M' ? s.rating_warn_mature_body : s.rating_warn_teen_body;
  const confirmLabel = rating === 'M' ? s.rating_warn_confirm_mature : s.rating_warn_confirm_teen;
  const colors = RATING_COLORS[rating] ?? RATING_COLORS['T'];
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Dialog open PaperProps={{ sx: { borderRadius: '16px', maxWidth: 440, border: `1.5px solid ${colors.border}`, background: '#0F0B1A' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px',
            background: colors.border, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 900, fontFamily: 'monospace',
          }}>
            {rating}
          </Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ background: colors.bg, mx: 2, mb: 1, borderRadius: '10px', py: 1.5 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: 14, lineHeight: 1.65, mb: 1.5 }}>{body}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{
                color: 'rgba(255,255,255,0.55)',
                '&.Mui-checked': { color: colors.border },
                py: 0.5,
              }}
            />
          }
          label={
            <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: 600 }}>
              {confirmLabel}
            </Typography>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', borderRadius: '10px', textTransform: 'none', '&:hover': { borderColor: 'rgba(255,255,255,0.5)' } }}>
          {s.rating_warn_go_back}
        </Button>
        <Button
          onClick={onContinue}
          variant="contained"
          disabled={!confirmed}
          sx={{
            background: colors.border,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { filter: 'brightness(1.1)' },
            '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
          }}
        >
          {s.rating_warn_continue}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const useGameModal = (): GameModalContextType => {
  const context = useContext(GameModalContext);
  if (context === undefined) {
    throw new Error('useGameModal must be used within a GameModalProvider');
  }
  return context;
}; 